import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UserIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DocumentChartBarIcon,
  ArrowRightIcon,
  CogIcon,
  BuildingOfficeIcon,
  CurrencyYenIcon,
  ChartBarIcon,
  CalendarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

// API base is provided by shared config (CRA/Next compatible)
const API = `${API_BASE}`;

// 安全な文字列化
const safeText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch (_) { return String(v); }
};

const EmployerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [employerProfile, setEmployerProfile] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    pendingRecruitments: 0,
    completedJobs: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }

    fetchEmployerData();
  }, [user, navigate]);

  const fetchEmployerData = async () => {
    try {
      setLoading(true);

      // 事業者プロフィール取得
      const profileResponse = await fetch(`${API}/employer-profiles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (profileResponse.ok) {
        const profiles = await profileResponse.json();
        const myProfile = profiles.find(p => p.employer_id === user.id);
        setEmployerProfile(myProfile);
      }
      try {
        const statsRes = await fetch(`${API}/employers/dashboard-stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (statsRes.ok) {
          const s = await statsRes.json();
          setDashboardStats({
            activeJobs: Number(s.activeJobs) || 0,
            totalApplications: Number(s.totalApplications) || 0,
            pendingRecruitments: Number(s.pendingRecruitments) || 0,
            completedJobs: Number(s.completedJobs) || 0
          });
        } else {
          setDashboardStats({
            activeJobs: 0,
            totalApplications: 0,
            pendingRecruitments: 0,
            completedJobs: 0
          });
        }
      } catch (_) {
        setDashboardStats({
          activeJobs: 0,
          totalApplications: 0,
          pendingRecruitments: 0,
          completedJobs: 0
        });
      }

    } catch (err) {
      console.error('Error fetching employer data:', err);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      id: 'account',
      title: 'ログイン / アカウント設定',
      description: '事業者プロフィール・認証設定・支払い情報の管理',
      icon: UserIcon,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      path: '/employer/account-settings'
    },
    {
      id: 'jobs',
      title: '求人案件の登録・編集',
      description: '新規求人作成・既存求人編集・緊急求人設定',
      icon: ClipboardDocumentListIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      path: '/job-management'
    },
    {
      id: 'recruitment',
      title: '募集状況の確認',
      description: '応募状況・募集ステータス・シフト確定者の管理',
      icon: EyeIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      path: '/employer/recruitment-status'
    },
    {
      id: 'work-history',
      title: '勤務履歴 / 報酬管理',
      description: '勤務実績・ワーカー評価・報酬支払い・手数料管理',
      icon: ClockIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      path: '/employer/work-history'
    },
    {
      id: 'messages',
      title: '求職者へのメッセージ機能',
      description: '面接不要でも補足連絡・当日緊急連絡先管理',
      icon: ChatBubbleLeftRightIcon,
      iconColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      path: '/employer/messages'
    },
    {
      id: 'reports',
      title: '絞り込み・レポート機能',
      description: 'CSVダウンロード・採用コスト集計・レビュー履歴',
      icon: DocumentChartBarIcon,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      path: '/employer/reports'
    },
    {
      id: 'plan',
      title: '有料プラン / サブスクリプション',
      description: 'スカウト機能の購入・有効期限の確認',
      icon: SparklesIcon,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      path: '/employer/plan'
    }
  ];

  const quickActionItems = [
    {
      title: '求人を投稿',
      description: '新しい求人案件を作成',
      icon: BuildingOfficeIcon,
      color: 'bg-blue-600 hover:bg-blue-700',
      path: '/create-job'
    },
    {
      title: '応募者確認',
      description: '最新の応募状況を確認',
      icon: ClipboardDocumentListIcon,
      color: 'bg-green-600 hover:bg-green-700',
      path: '/employer/recruitment-status'
    },
    {
      title: '報酬支払い',
      description: '完了した勤務の報酬支払い',
      icon: CurrencyYenIcon,
      color: 'bg-orange-600 hover:bg-orange-700',
      path: '/employer/payments'
    },
    {
      title: 'レポート',
      description: '採用データの分析',
      icon: ChartBarIcon,
      color: 'bg-purple-600 hover:bg-purple-700',
      path: '/employer/reports'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">事業者ダッシュボード</h1>
          <p className="text-gray-600 mt-2">
            {safeText(employerProfile ? employerProfile.business_name : user.name)} さん、お疲れ様です
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/business/add-card')}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              クレジットカードを登録する
            </button>
          </div>
        </div>

        {/* 統計情報 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">募集中求人</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.activeJobs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ClipboardDocumentListIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">応募者数</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalApplications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ClockIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">承認待ち</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.pendingRecruitments}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">完了案件</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.completedJobs}</p>
              </div>
            </div>
          </div>
        </div>

        {/* クイックアクション */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">クイックアクション</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActionItems.map((item, index) => (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className={`${item.color} text-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105`}
              >
                <div className="flex flex-col items-center text-center">
                  <item.icon className="w-8 h-8 mb-2" />
                  <span className="text-sm font-medium">{item.title}</span>
                  <span className="text-xs opacity-90 mt-1">{item.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* メニュー項目 */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">管理メニュー</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {menuItems.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`bg-white rounded-lg border-2 ${item.borderColor} p-6 cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
              >
                <div className="flex items-start">
                  <div className={`${item.bgColor} p-3 rounded-lg`}>
                    <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {item.description}
                    </p>
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      <span>詳細を見る</span>
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 事業者プロフィール確認 */}
        {!employerProfile && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <CogIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-yellow-800">
                  事業者プロフィールを設定してください
                </h3>
                <p className="text-yellow-700 mt-1">
                  求人投稿や応募者管理を行うには、事業者プロフィールの設定が必要です。
                </p>
              </div>
              <button
                onClick={() => navigate('/employer-profile/create')}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
              >
                プロフィール設定
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerDashboard;